import { Component, EventEmitter, HostListener, Input, Output } from '@angular/core';
import { MessageService } from 'primeng/api';

@Component({
  selector: 'app-date-range-popup',
  templateUrl: './date-range-popup.component.html',
  styleUrls: ['./date-range-popup.component.scss']
})
export class DateRangePopupComponent {

  selectedDateRange: Date[] = [];
  show_DateRangeDialog: boolean = false;
  dialog_header: string = "Select Delivery Date Range";
  minDate: Date;
  maxDate: Date | null = null;

  /**
   * Maximum number of days back from current date that can be selected.
   * Set to null for no restriction (uses default 2 years back).
   * For example, set to 90 to only allow dates from today to 90 days ago.
   */
  @Input() maxDaysBack: number | null = null;

  @Output() onDateRangeSelect = new EventEmitter<{delivery_date_from: string, delivery_date_to: string}>();

  @HostListener('document:keydown.escape', ['$event'])
  onEscPressed(event: KeyboardEvent) {
    this.hide();
  }

  constructor(private messageService: MessageService) {
    // Default minimum date to 2 years back from current date
    this.minDate = new Date();
    this.minDate.setFullYear(this.minDate.getFullYear() - 2);
  }

  show(header?: string) {
    // Apply date restrictions based on maxDaysBack input
    if (this.maxDaysBack !== null && this.maxDaysBack > 0) {
      // Set maxDate to today (can't select future dates)
      this.maxDate = new Date();
      this.maxDate.setHours(23, 59, 59, 999);

      // Set minDate to maxDaysBack days ago
      this.minDate = new Date();
      this.minDate.setDate(this.minDate.getDate() - this.maxDaysBack);
      this.minDate.setHours(0, 0, 0, 0);
    } else {
      // Default: 2 years back, no max date restriction
      this.minDate = new Date();
      this.minDate.setFullYear(this.minDate.getFullYear() - 2);
      this.maxDate = null;
    }

    if (header) {
      this.dialog_header = header;
    } else {
      this.dialog_header = "Select Delivery Date Range";
    }
    this.show_DateRangeDialog = true;
  }

  hide() {
    this.show_DateRangeDialog = false;
    this.selectedDateRange = [];
  }

  applyDateRange() {
    if (!this.isValidRange) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Warning',
        detail: 'Please select a valid delivery date range',
        key: 'app_notiy'
      });
      return;
    }

    const fromDate = this.selectedDateRange[0];
    const toDate = this.selectedDateRange[1];

    if (fromDate > toDate) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'From date cannot be later than To date',
        key: 'app_notiy'
      });
      return;
    }

    // Format dates for API (YYYY-MM-DD)
    const delivery_date_from = this.formatDateForAPI(fromDate);
    const delivery_date_to = this.formatDateForAPI(toDate);

    this.onDateRangeSelect.emit({
      delivery_date_from,
      delivery_date_to
    });

    this.hide();
  }

  private formatDateForAPI(date: Date): string {
    const d = new Date(date);
    let month = '' + (d.getMonth() + 1);
    let day = '' + d.getDate();
    const year = d.getFullYear();

    if (month.length < 2) month = '0' + month;
    if (day.length < 2) day = '0' + day;

    return [year, month, day].join('-');
  }

  get isValidRange(): boolean {
    return !!(this.selectedDateRange && this.selectedDateRange.length === 2 &&
           this.selectedDateRange[0] && this.selectedDateRange[1]);
  }

  get fromDate(): Date | null {
    return this.selectedDateRange && this.selectedDateRange.length > 0 ? this.selectedDateRange[0] : null;
  }

  get toDate(): Date | null {
    return this.selectedDateRange && this.selectedDateRange.length > 1 ? this.selectedDateRange[1] : null;
  }
}
